import React from 'react';
import { Button, Input, Popover } from 'antd';
import { SearchOutlined, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { ReviewData } from '../../../UploadReviewsListModal/types';
import EditReviewModal from '../../../UploadReviewsListModal/components/EditReviewModal';

type Props = {
    reviews: ReviewData[];
    onUpdate: (reviews: ReviewData[]) => void;
};

type SortOrder = 'asc' | 'desc' | null;

const ConfirmStep = (props: Props) => {
    const { reviews, onUpdate } = props;
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
    const [showEditModal, setShowEditModal] = React.useState<boolean>(false);
    const [searchText, setSearchText] = React.useState<string>('');
    const [sortOrder, setSortOrder] = React.useState<SortOrder>(null);
    const [showSearchPopover, setShowSearchPopover] = React.useState<boolean>(false);

    const handleRemove = (index: number) => {
        const newReviews = reviews.filter((_, i) => i !== index);
        onUpdate(newReviews);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setShowEditModal(true);
    };

    const handleSaveEdit = (updatedReview: ReviewData) => {
        if (editingIndex !== null) {
            const newReviews = [...reviews];
            newReviews[editingIndex] = updatedReview;
            onUpdate(newReviews);
        }
        setShowEditModal(false);
        setEditingIndex(null);
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setEditingIndex(null);
    };

    // 处理搜索
    const handleSearch = (value: string) => {
        setSearchText(value);
    };

    // 处理排序
    const handleSort = () => {
        if (sortOrder === null) {
            setSortOrder('desc');
        } else if (sortOrder === 'desc') {
            setSortOrder('asc');
        } else {
            setSortOrder(null);
        }
    };

    // 过滤和排序后的数据
    const filteredAndSortedReviews = React.useMemo(() => {
        let result = [...reviews];

        // 搜索过滤
        if (searchText.trim()) {
            result = result.filter((review) =>
                review.reviewContent?.toLowerCase().includes(searchText.toLowerCase()),
            );
        }

        // 排序
        if (sortOrder) {
            result.sort((a, b) => {
                const dateA = new Date(a.date || '').getTime();
                const dateB = new Date(b.date || '').getTime();
                if (sortOrder === 'asc') {
                    return dateA - dateB;
                } else {
                    return dateB - dateA;
                }
            });
        }

        return result;
    }, [reviews, searchText, sortOrder]);

    return (
        <div className={s.wrap}>
            {reviews.length > 0 && (
                <div className={s.tableWrap}>
                    <div className={s.table}>
                        <div className={s.tableHeader}>
                            <div className={s.colNumber} />
                            <div className={s.colContent}>
                                <div className={s.headerContent}>
                                    <span>Review details</span>
                                    <Popover
                                        content={
                                            <div className={s.searchPopover}>
                                                <Input
                                                    placeholder="Search reviews..."
                                                    value={searchText}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    prefix={<SearchOutlined />}
                                                    allowClear
                                                />
                                            </div>
                                        }
                                        trigger="click"
                                        open={showSearchPopover}
                                        onOpenChange={setShowSearchPopover}
                                        placement="bottomLeft"
                                    >
                                        <SearchOutlined className={s.searchIcon} />
                                    </Popover>
                                </div>
                            </div>
                            <div className={s.colDate}>
                                <div className={s.headerContent}>
                                    <span>Date</span>
                                    <div className={s.sortIcon} onClick={handleSort}>
                                        {sortOrder === 'desc' && <ArrowDownOutlined />}
                                        {sortOrder === 'asc' && <ArrowUpOutlined />}
                                        {sortOrder === null && (
                                            <ArrowDownOutlined className={s.sortIconInactive} />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={s.colRemove} />
                            <div className={s.colEdit} />
                        </div>
                        {filteredAndSortedReviews.map((review, displayIndex) => {
                            // 找到原始索引，用于删除和编辑
                            const originalIndex = reviews.findIndex((r) => r === review);
                            return (
                                <React.Fragment key={originalIndex}>
                                    <div className={s.tableRow}>
                                        <div className={s.colNumber}>{displayIndex + 1}</div>
                                        <div className={s.colContent}>{review.reviewContent || '-'}</div>
                                        <div className={s.colDate}>{review.date || '-'}</div>
                                        <div className={s.colRemove}>
                                            <Button
                                                type="link"
                                                onClick={() => handleRemove(originalIndex)}
                                                className={s.actionBtn}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                        <div className={s.colEdit}>
                                            <Button
                                                type="link"
                                                onClick={() => handleEdit(originalIndex)}
                                                className={s.actionBtn}
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
            <EditReviewModal
                show={showEditModal}
                review={editingIndex !== null ? reviews[editingIndex] : undefined}
                onCancel={handleCancelEdit}
                onSave={handleSaveEdit}
            />
        </div>
    );
};

export default ConfirmStep;
